/**
 * Browser polyfills for Node.js modules
 * Required for wallet libraries that expect Node.js globals
 */
import { Buffer } from 'buffer';

// Make Buffer available globally
window.Buffer = Buffer;
globalThis.Buffer = Buffer;

// Ensure global is defined
if (typeof global === 'undefined') {
  (window as any).global = window;
}

// Ensure process.env is defined
if (typeof process === 'undefined') {
  (window as any).process = { env: {} };
}

/**
 * Wraps an EIP-1193 provider in a safe facade whose `.on` and `.removeListener`
 * methods never violate ECMAScript Proxy [[Get]] invariants when Brave Wallet or
 * multi-wallet extensions define `on` / `removeListener` as non-configurable,
 * read-only data properties on a Proxy target.
 */
const SAFE_PROVIDER_CACHE = new WeakMap<object, any>();

let eip6963BraveProvider: any = null;

export function getEip6963BraveProvider(): any {
  return eip6963BraveProvider;
}

export function resolveBraveWalletProvider(): any {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('eip6963:requestProvider'));
  }
  if (eip6963BraveProvider) {
    return toSafeEip1193Provider(eip6963BraveProvider);
  }
  if (typeof window !== 'undefined') {
    try {
      const bw = (window as any).braveEthereum;
      if (bw) return toSafeEip1193Provider(bw);
    } catch {
      /* ignore */
    }
    try {
      const eth = (window as any).ethereum;
      if (eth?.providers?.length) {
        const found = eth.providers.find((p: any) => {
          try {
            return Boolean(p?.isBraveWallet);
          } catch {
            return false;
          }
        });
        if (found) return toSafeEip1193Provider(found);
      }
      if (eth?.isBraveWallet) {
        return toSafeEip1193Provider(eth);
      }
    } catch {
      /* ignore */
    }
  }
  return null;
}

export function toSafeEip1193Provider<T>(raw: T): T {
  if (!raw || (typeof raw !== 'object' && typeof raw !== 'function')) {
    return raw;
  }
  const obj = raw as unknown as Record<string, any>;
  try {
    if (obj.__cotiSafeEip1193) {
      return raw;
    }
  } catch {
    // Ignore Proxy trap errors on custom marker property
  }
  const cached = SAFE_PROVIDER_CACHE.get(obj);
  if (cached) {
    return cached;
  }

  const safeGet = (key: string): any => {
    try {
      return obj[key];
    } catch {
      return undefined;
    }
  };

  const safeInvokeEventMethod = (
    methods: readonly string[],
    event: string,
    listener: (...args: any[]) => void,
  ): void => {
    for (const methodName of methods) {
      try {
        const fn = obj[methodName];
        if (typeof fn === 'function') {
          fn.call(obj, event, listener);
          return;
        }
      } catch {
        // Proxy invariant TypeError on `obj[methodName]` — try next fallback
      }
    }
  };

  const facade: Record<string, any> = {
    __cotiSafeEip1193: true,
    request: (args: { method: string; params?: unknown }) => {
      const reqFn = safeGet('request');
      if (typeof reqFn === 'function') {
        return reqFn.call(obj, args);
      }
      return Promise.reject(new Error('EIP-1193 provider.request is unavailable'));
    },
    on: (event: string, listener: (...args: any[]) => void) => {
      safeInvokeEventMethod(['on', 'addListener', 'addEventListener'], event, listener);
      return facade;
    },
    removeListener: (event: string, listener: (...args: any[]) => void) => {
      safeInvokeEventMethod(['removeListener', 'off', 'removeEventListener'], event, listener);
      return facade;
    },
    addListener: (event: string, listener: (...args: any[]) => void) => {
      safeInvokeEventMethod(['addListener', 'on', 'addEventListener'], event, listener);
      return facade;
    },
    off: (event: string, listener: (...args: any[]) => void) => {
      safeInvokeEventMethod(['off', 'removeListener', 'removeEventListener'], event, listener);
      return facade;
    },
  };

  for (const flag of ['isMetaMask', 'isRabby', 'isPhantom', 'isBraveWallet', 'chainId', 'selectedAddress']) {
    Object.defineProperty(facade, flag, {
      configurable: true,
      enumerable: true,
      get: () => safeGet(flag),
    });
  }

  Object.defineProperty(facade, 'providers', {
    configurable: true,
    enumerable: true,
    get: () => {
      const list = safeGet('providers');
      return Array.isArray(list) ? list.map((p) => toSafeEip1193Provider(p)) : undefined;
    },
  });

  SAFE_PROVIDER_CACHE.set(obj, facade);
  return facade as unknown as T;
}

if (typeof window !== 'undefined') {
  (window as any).__cotiToSafeEip1193Provider = toSafeEip1193Provider;

  // Intercept EIP-6963 provider announcements before any listener receives them
  // so every announced provider has a Proxy-safe `.on` / `.removeListener` facade.
  const origAddEventListener = window.addEventListener.bind(window);
  const origRemoveEventListener = window.removeEventListener.bind(window);
  const listenerMap = new WeakMap<EventListenerOrEventListenerObject, EventListener>();

  window.addEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions,
  ) {
    if (type === 'eip6963:announceProvider' && listener) {
      let wrapped = listenerMap.get(listener);
      if (!wrapped) {
        wrapped = (event: Event) => {
          const custom = event as CustomEvent<{ info?: { rdns?: string; name?: string }; provider?: unknown }>;
          const detail = custom?.detail;
          if (detail && detail.provider) {
            const safeProvider = toSafeEip1193Provider(detail.provider);
            const rdns = (detail.info?.rdns || '').toLowerCase();
            if (rdns === 'com.brave.wallet' || rdns.includes('brave') || (safeProvider as any)?.isBraveWallet) {
              eip6963BraveProvider = safeProvider;
            }
            const safeDetail = {
              ...detail,
              provider: safeProvider,
            };
            const patchedEvent = new CustomEvent('eip6963:announceProvider', {
              detail: safeDetail,
            });
            if (typeof listener === 'function') {
              listener.call(window, patchedEvent);
            } else {
              listener.handleEvent(patchedEvent);
            }
            return;
          }
          if (typeof listener === 'function') {
            listener.call(window, event);
          } else {
            listener.handleEvent(event);
          }
        };
        listenerMap.set(listener, wrapped);
      }
      return origAddEventListener(type, wrapped, options);
    }
    return origAddEventListener(type, listener as EventListener, options);
  };

  window.removeEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions,
  ) {
    if (type === 'eip6963:announceProvider' && listener) {
      const wrapped = listenerMap.get(listener);
      if (wrapped) {
        return origRemoveEventListener(type, wrapped, options);
      }
    }
    return origRemoveEventListener(type, listener as EventListener, options);
  };

  // Proactively register a listener so eip6963BraveProvider is populated immediately on load
  window.addEventListener('eip6963:announceProvider', (() => {}) as EventListener);
  window.dispatchEvent(new Event('eip6963:requestProvider'));
}
